import { 
  collection, 
  updateDoc,
  doc,
  query, 
  where, 
  orderBy,
  getDocs,
  Timestamp,
  onSnapshot,
  getDoc,
  writeBatch,
  increment
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * Milestone Service
 * Handles all milestone-related operations
 */

// Create a new milestone
export const createMilestone = async (milestoneData) => {
  try {
    const milestone = {
      ...milestoneData,
      status: milestoneData.status || 'pending',
      urgent: milestoneData.urgent || false,
      files: milestoneData.files || [],
      order: milestoneData.order || 0,
      completedAt: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const batch = writeBatch(db);
    const milestoneRef = collection(db, 'milestones');
    const newMilestoneDocRef = doc(milestoneRef);

    batch.set(newMilestoneDocRef, milestone);

    const projectRef = doc(db, 'projects', milestoneData.projectId);
    batch.update(projectRef, {
      totalMilestones: increment(1),
      completedMilestones: milestone.status === 'completed' ? increment(1) : increment(0)
    });

    await batch.commit();

    return { success: true, id: newMilestoneDocRef.id };
  } catch (error) {
    console.error('Error creating milestone:', error);
    return { success: false, error: error.message };
  }
};

// Update a milestone
export const updateMilestone = async (milestoneId, updates) => {
  try {
    const milestoneRef = doc(db, 'milestones', milestoneId);
    
    const currentMilestoneSnap = await getDoc(milestoneRef);
    if (!currentMilestoneSnap.exists()) {
      return { success: false, error: 'Milestone not found' };
    }
    const currentMilestoneData = currentMilestoneSnap.data();
    const wasCompleted = currentMilestoneData.status === 'completed';
    const isCompleted = updates.status === 'completed';
    const projectId = currentMilestoneData.projectId;

    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    if (updates.status === 'completed' && !updates.completedAt) {
      updateData.completedAt = Timestamp.now();
    }

    if (updates.status && updates.status !== 'completed') {
      updateData.completedAt = null;
    }

    const batch = writeBatch(db);
    batch.update(milestoneRef, updateData);

    if (projectId && wasCompleted !== isCompleted) {
      batch.update(doc(db, 'projects', projectId), {
        completedMilestones: increment(isCompleted ? 1 : -1)
      });
    }

    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error('Error updating milestone:', error);
    return { success: false, error: error.message };
  }
};

// Delete a milestone
export const deleteMilestone = async (milestoneId) => {
  try {
    const milestoneRef = doc(db, 'milestones', milestoneId);
    const currentMilestoneSnap = await getDoc(milestoneRef);
    if (!currentMilestoneSnap.exists()) {
      return { success: false, error: 'Milestone not found' };
    }
    const currentMilestoneData = currentMilestoneSnap.data();
    const wasCompleted = currentMilestoneData.status === 'completed';
    const projectId = currentMilestoneData.projectId;

    // Delete associated files from Storage
    const filesQuery = query(
      collection(db, 'files'),
      where('milestoneId', '==', milestoneId)
    );
    const filesSnapshot = await getDocs(filesQuery);

    await Promise.all(filesSnapshot.docs.map(async (fileDoc) => {
      const fileData = fileDoc.data();
      if (fileData.storagePath) {
        try {
          await deleteObject(ref(storage, fileData.storagePath));
        } catch (e) {
          if (e.code !== 'storage/object-not-found') console.error(e);
        }
      }
    }));

    const batch = writeBatch(db);

    // Delete file records from Firestore and update storage counters
    filesSnapshot.docs.forEach((fileDoc) => {
      const fileData = fileDoc.data();
      if (fileData.clientId && fileData.fileSize) {
        batch.update(doc(db, 'users', fileData.clientId), {
          storageUsed: increment(-fileData.fileSize)
        });
      }
      batch.delete(fileDoc.ref);
    });

    batch.delete(milestoneRef);

    if (projectId) {
      batch.update(doc(db, 'projects', projectId), {
        totalMilestones: increment(-1),
        completedMilestones: wasCompleted ? increment(-1) : increment(0)
      });
    }

    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Error deleting milestone:', error);
    return { success: false, error: error.message };
  }
};

// Get all milestones for a client
export const getClientMilestones = async (clientId) => {
  try {
    const q = query(
      collection(db, 'milestones'),
      where('clientId', '==', clientId),
      orderBy('order', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting client milestones:', error);
    return [];
  }
};

// Get milestones for a specific project
export const getProjectMilestones = async (projectId) => {
  try {
    const q = query(
      collection(db, 'milestones'),
      where('projectId', '==', projectId),
      orderBy('order', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting project milestones:', error);
    return [];
  }
};

// Subscribe to client milestones (real-time)
export const subscribeToClientMilestones = (clientId, callback) => {
  const q = query(
    collection(db, 'milestones'),
    where('clientId', '==', clientId),
    orderBy('order', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  }, (error) => {
    console.error('Error subscribing to milestones:', error);
    callback([]);
  });
};

// Subscribe to project milestones (real-time)
export const subscribeToProjectMilestones = (projectId, callback) => {
  const q = query(
    collection(db, 'milestones'),
    where('projectId', '==', projectId),
    orderBy('order', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  }, (error) => {
    console.error('Error subscribing to project milestones:', error);
    callback([]);
  });
};

// Get all milestones (admin)
export const getAllMilestones = async () => {
  try {
    const q = query(
      collection(db, 'milestones'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting all milestones:', error);
    return [];
  }
};

// Add file to milestone
export const addFileToMilestone = async (milestoneId, fileData) => {
  try {
    const milestoneRef = doc(db, 'milestones', milestoneId);
    const milestoneSnap = await getDoc(milestoneRef);
    
    if (!milestoneSnap.exists()) {
      throw new Error('Milestone not found');
    }

    const currentMilestone = milestoneSnap.data();
    const currentFiles = currentMilestone.files || [];

    await updateDoc(milestoneRef, {
      files: [...currentFiles, { ...fileData, uploadedAt: Timestamp.now() }],
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error adding file to milestone:', error);
    return { success: false, error: error.message };
  }
};

// Remove file from milestone
export const removeFileFromMilestone = async (milestoneId, fileUrl) => {
  try {
    const milestoneRef = doc(db, 'milestones', milestoneId);
    const milestoneSnap = await getDoc(milestoneRef);
    
    if (!milestoneSnap.exists()) {
      throw new Error('Milestone not found');
    }

    const currentMilestone = milestoneSnap.data();
    const currentFiles = currentMilestone.files || [];

    await updateDoc(milestoneRef, {
      files: currentFiles.filter(file => file.url !== fileUrl),
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing file from milestone:', error);
    return { success: false, error: error.message };
  }
};

// Calculate project progress
export const calculateProgress = (milestones) => {
  if (!milestones || milestones.length === 0) return 0;
  const completed = milestones.filter(m => m.status === 'completed').length;
  return Math.round((completed / milestones.length) * 100);
};

const milestoneService = {
  createMilestone,
  updateMilestone,
  deleteMilestone,
  getClientMilestones,
  getProjectMilestones,
  subscribeToClientMilestones,
  subscribeToProjectMilestones,
  getAllMilestones,
  addFileToMilestone,
  removeFileFromMilestone,
  calculateProgress
};

export default milestoneService;